import {
  PageWrapper,
  PENDING_MILESTONE_APPROVAL_DASHBOARD_PARAMS,
  UserDisplayName,
} from "@/components";
import DashboardTableFullView from "@/components/Dashboard/DashboardTableFullView";
import { pendingMilestoneApprovalsFieldNames } from "@/enums";
import { routes } from "@/helpers";
import { useColumnFactory } from "@/hooks/useColumnFactory";
import {
  useGetRoutingMilestoneApprovalsQuery,
  useGetUsersByIdsMutation,
} from "@/services";
import { useEffect } from "react";
import { useIntl } from "react-intl";
import { useQueryParams } from "use-query-params";

const PAGINATION_PAGE_SIZE = 10;

const RoutingMilestoneApprovalsFullView = () => {
  const intl = useIntl();
  const [queryParams, setQueryParams] = useQueryParams({
    ...PENDING_MILESTONE_APPROVAL_DASHBOARD_PARAMS,
  });

  const {
    createCustomSortableSmallerNumColumn,
    createFilterableSortableNumberColumn,
    createFilterableCustomSortableNameColumn,
    createFilterableSortableSmallerNameColumn,
    createSortableDateColumn,
    createMilestoneViewLinkColumn,
    createFilterableSortableDropdownColumn,
    createFilterableSortableSmallerUserNameColumn,
    handleChange,
  } = useColumnFactory(queryParams, setQueryParams);

  const {
    data: { data: projectMilestonesRoutingApproval, totalCount } = {},
    isFetching,
  } = useGetRoutingMilestoneApprovalsQuery({
    ...queryParams,
    pageSize: PAGINATION_PAGE_SIZE,
  });

  const [getUsersByIds, { data: usersByIds }] = useGetUsersByIdsMutation();

  useEffect(() => {
    const ids = projectMilestonesRoutingApproval?.map(
      ({ approver }) => approver
    );

    if (!ids?.length) return;

    const uniqueProjectLeaderIds = [...new Set(ids)];
    getUsersByIds(uniqueProjectLeaderIds);
  }, [projectMilestonesRoutingApproval, getUsersByIds]);

  const getUserDisplay = (userId) => {
    if (!userId) return null;

    const userData = usersByIds?.find(({ id }) => id === userId);

    if (!userData) return null;

    return (
      <UserDisplayName
        displayName={userData?.displayName}
        userAccountEnabled={userData?.azureAdAccountEnabled}
        user={userData}
      />
    );
  };

  return (
    <PageWrapper
      pageTitle={intl.formatMessage({
        id: "dashboard.titles.routingMilestoneApprovals",
      })}
    >
      <DashboardTableFullView
        data={projectMilestonesRoutingApproval}
        isFetching={isFetching}
        title={intl.formatMessage({
          id: "dashboard.titles.routingMilestoneApprovals",
        })}
        titleRoute={routes.dashboardFullViews.routingMilestoneApprovals}
        totalCount={totalCount}
        columns={[
          createMilestoneViewLinkColumn(
            intl.formatMessage({ id: "dashboard.columns.milestone" }),
            intl.formatMessage({ id: "dashboard.columns.view" })
          ),
          createFilterableSortableNumberColumn(
            intl.formatMessage({ id: "dashboard.columns.projectNumber" }),
            pendingMilestoneApprovalsFieldNames.projectNumber
          ),
          createFilterableCustomSortableNameColumn(
            intl.formatMessage({ id: "dashboard.columns.projectName" }),
            pendingMilestoneApprovalsFieldNames.projectName,
            pendingMilestoneApprovalsFieldNames.projectNameNormalized
          ),
          createFilterableSortableSmallerNameColumn(
            intl.formatMessage({ id: "dashboard.columns.parentMilestone" }),
            pendingMilestoneApprovalsFieldNames.parentMilestone
          ),
          createFilterableSortableSmallerNameColumn(
            intl.formatMessage({ id: "dashboard.columns.milestone" }),
            pendingMilestoneApprovalsFieldNames.milestone
          ),
          createSortableDateColumn(
            intl.formatMessage({ id: "dashboard.columns.dateAssigned" }),
            pendingMilestoneApprovalsFieldNames.dateAssigned
          ),
          createFilterableSortableSmallerUserNameColumn(
            intl.formatMessage({ id: "dashboard.columns.approver" }),
            pendingMilestoneApprovalsFieldNames.approver,
            pendingMilestoneApprovalsFieldNames.approverName,
            getUserDisplay
          ),
          createFilterableSortableSmallerNameColumn(
            intl.formatMessage({ id: "dashboard.columns.schedule" }),
            pendingMilestoneApprovalsFieldNames.scheduleName
          ),
          createFilterableSortableDropdownColumn(
            intl.formatMessage({ id: "dashboard.columns.functionalArea" }),
            pendingMilestoneApprovalsFieldNames.functionalArea,
            pendingMilestoneApprovalsFieldNames.functionalAreaLabel
          ),
          createFilterableSortableSmallerNameColumn(
            intl.formatMessage({ id: "dashboard.columns.projectRole" }),
            pendingMilestoneApprovalsFieldNames.projectRoleName
          ),
          createCustomSortableSmallerNumColumn(
            intl.formatMessage({ id: "dashboard.columns.days" }),
            pendingMilestoneApprovalsFieldNames.daysAvailable,
            pendingMilestoneApprovalsFieldNames.dateAssigned
          ),
        ]}
        queryParams={queryParams}
        setQueryParams={setQueryParams}
        handleChange={handleChange}
        pageSize={PAGINATION_PAGE_SIZE}
      />
    </PageWrapper>
  );
};

export default RoutingMilestoneApprovalsFullView;
